import { PlayIcon } from '@heroicons/react/24/solid';
import {
  DividerStyled,
  LineStyled,
  OptionStackStyled,
  PlayIconStyled,
  RootStyled,
  VideoPlayerCardContainerStyled,
  VideoPlayerCardPlaceholderStyled,
  VideoPlayerCardStyled,
} from './video-card.styled';
import { IconButton, Stack, Typography, useTheme } from '@mui/material';
import { IconSize } from '@constants';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useRef, useState } from 'react';
import { formatDate } from '@lib/dateUtils';
import { t } from 'i18next';

interface Props {
  videoUrl: string | undefined;
  thumbnailUrl: string | undefined;
  createdAt: string | undefined;
  onSelect: (value: boolean) => void;
  isSelected: boolean;
  mode: 'create' | 'edit' | undefined;
}

export const VideoCard = ({
  videoUrl,
  createdAt,
  onSelect,
  thumbnailUrl,
  isSelected,
  mode,
}: Props) => {
  const theme = useTheme();
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoPlayerRef = useRef<HTMLVideoElement>(null);

  const pauseVideo = () => {
    if (!videoPlayerRef.current) return;
    try {
      videoPlayerRef.current?.pause();
    } catch (e) {
      console.error(e);
    }
  };

  const playVideo = async () => {
    if (!videoPlayerRef.current) return;
    try {
      await videoPlayerRef.current?.play();
    } catch (e) {
      console.error(e);
    }
  };

  const onVideoPlayerClick = () => {
    if (!videoPlaying) playVideo();
    else pauseVideo();
    setVideoPlaying((prev) => !prev);
  };

  return (
    <RootStyled>
      <OptionStackStyled
        selected={false}
        clickable={false}
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        gap={2}
        p={2}
      >
        <Stack direction={'row'} gap={2} alignItems={'center'}>
          <VideoPlayerCardPlaceholderStyled>
            <PlayIcon
              color={theme.palette.white.main}
              height={IconSize.buttonIcon}
            />
          </VideoPlayerCardPlaceholderStyled>
          <Typography variant="h4">
            {mode === 'create' ? t('ADD_VIDEO') : t('UPDATE_VIDEO')}
          </Typography>
        </Stack>
        <IconButton data-qa="go-upload" onClick={() => onSelect(false)}>
          <ArrowRightIcon
            width={IconSize.md}
            color={theme.palette.primary.main}
          />
        </IconButton>
      </OptionStackStyled>

      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <LineStyled />
        <DividerStyled p={2}>OU</DividerStyled>
        <LineStyled />
      </Stack>

      <OptionStackStyled
        selected={isSelected}
        clickable={true}
        data-qa="establishment-video-card"
        onClick={() => onSelect(true)}
        alignItems={'flex-start'}
        justifyContent={'flex-start'}
        gap={2}
        p={2}
      >
        <Stack
          direction={'row'}
          gap={2}
          alignItems={'center'}
          justifyContent={'flex-start'}
        >
          <VideoPlayerCardContainerStyled>
            {!videoPlaying && (
              <PlayIconStyled
                data-qa="click-video-player"
                onClick={onVideoPlayerClick}
                color={theme.palette.white.main}
                height={IconSize.buttonIcon}
              />
            )}
            <VideoPlayerCardStyled
              data-qa="on-video-player-click"
              ref={videoPlayerRef}
              height={'100%'}
              onClick={onVideoPlayerClick}
              poster={thumbnailUrl}
            >
              <source src={videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </VideoPlayerCardStyled>
          </VideoPlayerCardContainerStyled>
          <Stack>
            <Typography variant="h4">
              Utiliser la vidéo de l’entreprise
            </Typography>
            {createdAt && (
              <Typography variant="caption">
                Ajoutée le {formatDate(createdAt)}
              </Typography>
            )}
          </Stack>
        </Stack>
      </OptionStackStyled>
    </RootStyled>
  );
};
